import { z } from "zod";
import { MAILBOX_PURPOSES } from "./roles";

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Hex szín, pl. #F25C1F");
const email = z.string().trim().toLowerCase().email("Érvénytelen e-mail cím");
export const passwordSchema = z
  .string()
  .min(10, "Legalább 10 karakter")
  .max(200)
  .refine((v) => /[a-zA-ZáéíóöőúüűÁÉÍÓÖŐÚÜŰ]/.test(v) && /\d/.test(v), "Legyen benne betű és szám is");

export const loginSchema = z.object({ email, password: z.string().min(1).max(200) });
export const forgotPasswordSchema = z.object({ email });
export const resetPasswordSchema = z.object({ token: z.string().min(20), password: passwordSchema });
export const acceptInvitationSchema = z.object({
  token: z.string().min(20),
  name: z.string().trim().min(2).max(120).optional(),
  password: passwordSchema.optional(),
});
export const changePasswordSchema = z.object({ currentPassword: z.string().min(1), newPassword: passwordSchema });
export const updateProfileSchema = z.object({ name: z.string().trim().min(2).max(120) });

export const inviteSchema = z.object({ email, name: z.string().trim().max(120).optional() });

export const createOrgSchema = z.object({
  name: z.string().trim().min(2).max(160),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9](?:[a-z0-9-]{0,48}[a-z0-9])?$/, "Csak kisbetű, szám és kötőjel")
    .optional(),
});

export const updateOrgSchema = z.object({
  name: z.string().trim().min(2).max(160).optional(),
  brandName: z.string().trim().max(160).nullable().optional(),
  primaryColor: hex.nullable().optional(),
  secondaryColor: hex.nullable().optional(),
  settings: z.record(z.unknown()).optional(),
});

export const orgDomainSchema = z.object({
  host: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^(?=.{3,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/, "Érvénytelen domain"),
  isPrimary: z.boolean().default(false),
});

export const mailboxSchema = z.object({
  label: z.string().trim().min(1).max(120),
  host: z.string().trim().min(1).max(255),
  port: z.coerce.number().int().min(1).max(65535),
  secure: z.boolean(),
  username: z.string().trim().min(1).max(255),
  /** Omitted on update = keep the stored one. */
  password: z.string().min(1).max(500).optional(),
  fromName: z.string().trim().max(160).nullable().optional(),
  fromAddress: email,
  purposes: z.array(z.enum(MAILBOX_PURPOSES)).default([]),
  isDefault: z.boolean().default(false),
});

/** Magyar irányítószám: pontosan 4 számjegy. Külföldi címnél (country ≠ Magyarország) nem kötjük meg. */
export const HU_ZIP = /^\d{4}$/;

const huZipCheck = (v: { country: string; zip: string }, ctx: z.RefinementCtx) => {
  if (v.country.trim().toLowerCase() === "magyarország" && !HU_ZIP.test(v.zip)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["zip"], message: "A magyar irányítószám 4 számjegy (pl. 1051)" });
  }
};

export const addressFields = z.object({
  label: z.string().trim().max(120).nullable().optional(),
  country: z.string().trim().max(80).default("Magyarország"),
  zip: z.string().trim().min(3).max(12),
  city: z.string().trim().min(1).max(120),
  street: z.string().trim().min(1).max(200),
  isPrimary: z.boolean().default(false),
});

export const addressSchema = addressFields.superRefine(huZipCheck);
/** Meglévő címek mentéséhez (id-vel). */
export const savedAddressSchema = addressFields.extend({ id: z.string().uuid().optional() }).superRefine(huZipCheck);

export const createPropertySchema = z.object({
  name: z.string().trim().min(2).max(160),
  code: z.string().trim().max(40).nullable().optional(),
  addresses: z.array(addressSchema).min(1, "Legalább egy cím kell"),
});

export const updatePropertySchema = z.object({
  name: z.string().trim().min(2).max(160).optional(),
  code: z.string().trim().max(40).nullable().optional(),
  lotNumber: z.string().trim().max(60).nullable().optional(),
  managementMode: z.enum(["OWNER_MANAGED", "THIRD_PARTY"]).optional(),
  defaultOwnerId: z.string().uuid().nullable().optional(),
  /** Gazdasági esemény kódja a könyveléshez (pl. „#SZK”); a számlatételekre kerül. */
  revenueEventCode: z.string().trim().max(40).nullable().optional(),
});

/** Ingatlanszintű beállítások (Property.settings jsonb); mindig a parsePropertySettings()-en át olvasva. */
export const propertySettingsSchema = z
  .object({
    revenueEventCode: z.string().nullable().default(null),
  })
  .passthrough();
export type PropertySettings = z.infer<typeof propertySettingsSchema>;
export function parsePropertySettings(raw: unknown): PropertySettings {
  const parsed = propertySettingsSchema.safeParse(raw ?? {});
  return parsed.success ? parsed.data : propertySettingsSchema.parse({});
}

/** Szuperadmin SQL konzol: egyetlen utasítás (a DO $$ … $$ blokk is egynek számít). */
export const adminSqlSchema = z.object({
  sql: z.string().trim().min(1, "Írj be egy SQL utasítást").max(100_000),
  /** Kifejezett megerősítés, hogy szuperadminként fut. */
  confirm: z.literal(true),
  /** Adatjavítás mód: a módosíthatatlanságot őrző triggerek engednek (app.correction). */
  correction: z.boolean().default(false),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type CreatePropertyInput = z.infer<typeof createPropertySchema>;
export type AddressInput = z.infer<typeof addressSchema>;
export type MailboxInput = z.infer<typeof mailboxSchema>;
